import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaArrowLeft, FaCcMastercard, FaCcVisa, FaCheckCircle, FaCreditCard, FaLock, FaSyncAlt, FaTimesCircle, FaWallet } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getCardBalance, getMembershipPlans, getWomenTrainingFee, initiateMembershipPurchase } from '../../../api/cardService';
import { initiateGeneralPayment } from '../../../api/paymentService';
import './CardApplicationForm.scss';

const initialPaymentData = {
  cardName: '',
  cardNumber: '',
  expiryMonth: '',
  expiryYear: '',
  cvv: ''
};

const WOMEN_TRAINING_PROGRAM = 'WomenTraining';

const normalizeProgramLabel = (programType) => {
  if (programType === 'Fitness') return 'Fitness';
  if (programType === WOMEN_TRAINING_PROGRAM) return 'Kadın Eğitim';
  return 'Havuz';
};

const CardBalanceLoad = ({ onSelectWomenTraining, onSelectCardApplication }) => {
  const [cardNumber, setCardNumber] = useState('');
  const [programType, setProgramType] = useState('Fitness');
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [cardInfo, setCardInfo] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentData, setPaymentData] = useState(initialPaymentData);
  const [paymentErrors, setPaymentErrors] = useState({});
  const [paymentHtml, setPaymentHtml] = useState(null);
  const [paymentOutcome, setPaymentOutcome] = useState(null);
  const [resultMessage, setResultMessage] = useState('');
  const [womenTrainingFee, setWomenTrainingFee] = useState(900);
  const iframeRef = useRef(null);

  useEffect(() => {
    const fetchPlans = async () => {
      const result = await getMembershipPlans();
      if (result?.isSuccess && Array.isArray(result.data)) {
        setMembershipPlans(result.data.filter((plan) => plan.isActive));
      }
    };

    fetchPlans();
  }, []);

  useEffect(() => {
    const fetchWomenTrainingFee = async () => {
      const result = await getWomenTrainingFee();
      if (result?.isSuccess && result?.data?.fee) {
        setWomenTrainingFee(Number(result.data.fee));
      }
    };

    fetchWomenTrainingFee();
  }, []);

  const availablePlans = useMemo(() => {
    if (programType === WOMEN_TRAINING_PROGRAM) {
      return [
        {
          id: 'women-training',
          programType: WOMEN_TRAINING_PROGRAM,
          displayName: 'Kadın Eğitim',
          price: womenTrainingFee,
          creditCount: 0,
          isActive: true
        }
      ];
    }

    return membershipPlans.filter((plan) => plan.programType === programType);
  }, [membershipPlans, programType, womenTrainingFee]);

  const selectedPlan = useMemo(
    () => availablePlans.find((plan) => String(plan.id) === String(selectedPlanId)),
    [availablePlans, selectedPlanId]
  );

  useEffect(() => {
    if (!selectedPlan && availablePlans.length > 0) {
      setSelectedPlanId(String(availablePlans[0].id));
    }
  }, [availablePlans, selectedPlan]);

  const detectCardType = (number) => {
    const cleaned = number.replace(/\s/g, '');
    if (cleaned.startsWith('4')) return '1';
    if (/^5[1-5]/.test(cleaned) || /^2[2-7]/.test(cleaned)) return '2';
    return null;
  };

  const luhnCheck = (value) => {
    let checksum = 0;
    let multiplier = 1;

    for (let i = value.length - 1; i >= 0; i -= 1) {
      let product = Number(value.charAt(i)) * multiplier;
      if (product > 9) {
        checksum += 1;
        product -= 10;
      }
      checksum += product;
      multiplier = multiplier === 1 ? 2 : 1;
    }

    return checksum % 10 === 0;
  };

  const handleLookupCard = async () => {
    if (!cardNumber.trim()) {
      toast.warn('Lütfen kart numarasını giriniz.');
      return null;
    }

    setLookupLoading(true);
    try {
      const result = await getCardBalance(cardNumber.trim());
      if (!result?.isSuccess) {
        setCardInfo(null);
        toast.error(result?.resultMessage || 'Kart bulunamadı.');
        return null;
      }

      // Sistemde olmayan eski kartlar için de işlem yapılabilir
      if (result.isUnregistered) {
        setCardInfo(result.data);
        toast.info(result.resultMessage || 'Eski kart tespit edildi. Bakiye yükleme işlemine devam edebilirsiniz.');
        return result.data;
      }

      setCardInfo(result.data);
      toast.success('Kart bilgileri bulundu.');
      return result.data;
    } finally {
      setLookupLoading(false);
    }
  };

  const handleContinue = async () => {
    if (!selectedPlan) {
      toast.warn('Lütfen üyelik paketi seçiniz.');
      return;
    }

    const resolvedCardInfo = cardInfo || await handleLookupCard();
    if (!resolvedCardInfo) {
      return;
    }

    setShowPaymentForm(true);
    setPaymentOutcome(null);
    setPaymentHtml(null);
    setResultMessage('');
  };

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    try {
      const href = iframe.contentWindow?.location?.href;
      if (!href) return;

      const iframeUrl = new URL(href);
      const paymentStatus = iframeUrl.searchParams.get('paymentStatus');
      const error = iframeUrl.searchParams.get('error');

      if (paymentStatus === 'success') {
        setPaymentOutcome('success');
        setResultMessage('Üyelik paketi satın alma işlemi başarıyla tamamlandı.');
        setPaymentHtml(null);
        handleLookupCard();
      } else if (paymentStatus === 'failed') {
        setPaymentOutcome('failed');
        setResultMessage(error ? decodeURIComponent(error) : 'Ödeme işlemi başarısız oldu.');
        setPaymentHtml(null);
      }
    } catch {
      // Bank iframe cross-origin
    }
  };

  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data;
      if (!data || data.type !== 'PAYMENT_RESULT') return;

      if (data.status === 'success') {
        setPaymentOutcome('success');
        setResultMessage('Üyelik paketi satın alma işlemi başarıyla tamamlandı.');
        setPaymentHtml(null);
        handleLookupCard();
      } else if (data.status === 'failed') {
        setPaymentOutcome('failed');
        setResultMessage(data.error ? decodeURIComponent(data.error) : 'Ödeme işlemi başarısız oldu.');
        setPaymentHtml(null);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [cardNumber]);

  const handlePaymentSubmit = async () => {
    const errors = {};
    const pan = paymentData.cardNumber.replace(/\s/g, '');
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    if (!selectedPlan) {
      toast.warn('Lütfen üyelik paketi seçiniz.');
      return;
    }

    if (!pan) {
      errors.cardNumber = 'Kart numarası gereklidir.';
    } else if (pan.length !== 16) {
      errors.cardNumber = 'Kart numarası 16 haneli olmalıdır.';
    } else if (!luhnCheck(pan)) {
      errors.cardNumber = 'Geçersiz kart numarası.';
    }

    if (!paymentData.cardName.trim()) {
      errors.cardName = 'Kart üzerindeki isim gereklidir.';
    }

    if (!paymentData.expiryMonth || !paymentData.expiryYear) {
      errors.expiry = 'Son kullanma tarihi seçiniz.';
    } else {
      const expYear = parseInt(paymentData.expiryYear, 10);
      const expMonth = parseInt(paymentData.expiryMonth, 10);
      if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
        errors.expiry = 'Kartın son kullanma tarihi geçmiş.';
      }
    }

    if (!paymentData.cvv) {
      errors.cvv = 'CVV gereklidir.';
    } else if (paymentData.cvv.length !== 3) {
      errors.cvv = 'CVV 3 haneli olmalıdır.';
    }

    if (Object.keys(errors).length > 0) {
      setPaymentErrors(errors);
      return;
    }

    setLoading(true);
    setPaymentErrors({});

    try {
      const initResult = await initiateMembershipPurchase({
        cardNumber: cardNumber.trim(),
        programType,
        membershipPlanId: programType === WOMEN_TRAINING_PROGRAM ? null : selectedPlan.id
      });

      if (!initResult?.isSuccess) {
        toast.error(initResult?.resultMessage || 'Üyelik paketi satın alma başlatılamadı.');
        return;
      }

      const paymentResult = await initiateGeneralPayment({
        amount: initResult.data.amount,
        paymentGroup: 'CardMembershipPurchase',
        referenceId: String(initResult.data.transactionId),
        description: initResult.data.description,
        programType,
        membershipPlanId: selectedPlan.id,
        pan,
        cv2: paymentData.cvv,
        expireMonth: paymentData.expiryMonth.padStart(2, '0'),
        expireYear: paymentData.expiryYear,
        cardType: detectCardType(paymentData.cardNumber),
        firstName: cardInfo?.firstName || paymentData.cardName.split(' ')[0],
        lastName: cardInfo?.lastName || paymentData.cardName.split(' ').slice(1).join(' ') || 'Kullanici',
        email: cardInfo?.email || ''
      });

      if (paymentResult?.isSuccess && paymentResult?.data?.html) {
        setPaymentHtml(paymentResult.data.html);
      } else {
        toast.error(paymentResult?.resultMessage || 'Ödeme başlatılamadı.');
      }
    } catch (error) {
      console.error(error);
      toast.error('Ödeme sırasında beklenmeyen bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const previewCardNumber = paymentData.cardNumber || '0000 0000 0000 0000';
  const previewCardHolder = paymentData.cardName || `${cardInfo?.firstName || ''} ${cardInfo?.lastName || ''}`.trim() || 'AD SOYAD';
  const previewExpiry = paymentData.expiryMonth && paymentData.expiryYear
    ? `${paymentData.expiryMonth}/${String(paymentData.expiryYear).slice(-2)}`
    : 'AA/YY';

  return (
    <div className="sa-card-application">
      <div className="sa-facility-details-header sa-unified-tab-header">
        <div className="sa-unified-tab-header-row">
          <h3 className="sa-unified-tab-title">
            <div className="sa-unified-tab-icon">
              <FaWallet size={20} />
            </div>
            Paket Satın Al / Yenile
          </h3>
          {selectedPlan && (
            <div className="sa-unified-tab-price-breakdown">
              <span className="sa-price-item">{normalizeProgramLabel(programType)} Paketi</span>
              {programType !== WOMEN_TRAINING_PROGRAM && (
                <>
                  <span className="sa-price-separator">+</span>
                  <span className="sa-price-item">{selectedPlan.creditCount} seans</span>
                </>
              )}
              <span className="sa-price-total">{Number(selectedPlan.price || 0).toFixed(2)} TL</span>
            </div>
          )}
        </div>
        <p className="sa-unified-tab-subtitle">
          Mevcut kartınıza yeni paket satın alabilir veya süresi dolan paketinizi yenileyebilirsiniz.
        </p>
      </div>

      {/* Uyarı Banner */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '14px 16px',
        background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
        border: '1px solid #fbbf24',
        borderRadius: '12px',
        marginBottom: '4px'
      }}>
        <span style={{ fontSize: '20px', flexShrink: 0, marginTop: '1px' }}>⚠️</span>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, color: '#92400e', fontSize: '0.9rem', lineHeight: '1.6' }}>
            <strong>Bu bölüm yalnızca mevcut kart sahibi üyeler içindir.</strong>
            {' '}Kartınız yoksa önce kart başvurusu yapmanız gerekmektedir.
          </p>
          {onSelectCardApplication && (
            <button
              type="button"
              onClick={onSelectCardApplication}
              style={{
                marginTop: '8px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 14px',
                background: '#d97706',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.85rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'background 0.15s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#b45309'}
              onMouseLeave={e => e.currentTarget.style.background = '#d97706'}
            >
              🪪 Kart Başvurusu Yap
            </button>
          )}
        </div>
      </div>

      {!showPaymentForm ? (
        <div className="sa-appointment-section">
          <div className="sa-booking-form sa-card-form" style={{ marginTop: '20px', border: 'none', background: 'transparent', boxShadow: 'none', padding: '10px 12px 14px' }}>
            <h4 style={{ fontSize: '0.85rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ width: '24px', height: '3px', background: '#e2e8f0', borderRadius: '2px', display: 'block' }}></span>
              Yükleme Bilgileri
            </h4>

            <div className="sa-form-grid">
              <div className="sa-form-group">
                <label>Yüklenecek Kart Numarası</label>
                <input
                  className="sa-form-input"
                  value={cardNumber}
                  onChange={(event) => setCardNumber(event.target.value)}
                  placeholder="Kart numaranızı giriniz"
                />
              </div>
              <div className="sa-form-group" style={{ gridColumn: '1 / -1' }}>
                <div style={{ padding: '12px 14px', borderRadius: '14px', background: 'linear-gradient(180deg, #eaf6ff 0%, #dcefff 100%)', border: '1px solid #b9daf9', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                  <div style={{ width: '96px', minWidth: '96px', height: '72px', borderRadius: '10px', background: 'linear-gradient(180deg, #ffffff 0%, #eef6ff 100%)', border: '1px solid #c9ddf3', boxShadow: '0 4px 10px rgba(15, 23, 42, 0.12)', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)', width: '16px', height: '6px', borderRadius: '0 0 4px 4px', background: '#d5e4f4' }}></div>
                    <div style={{ position: 'absolute', left: '10px', right: '10px', bottom: '14px', height: '16px', borderRadius: '4px', background: '#edf6ff', border: '1px solid #d3e5f6', display: 'flex', alignItems: 'center', padding: '0 6px', overflow: 'hidden' }}>
                      <span style={{ fontSize: '7px', color: '#2563eb', fontWeight: '700', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>0010599659 161,48363</span>
                    </div>
                  </div>
                  <div style={{ flex: '1 1 260px' }}>
                    <strong style={{ display: 'block', color: '#0f3d75', marginBottom: '6px', fontSize: '0.95rem' }}>Kart numaranız nerede?</strong>
                    <span style={{ color: '#24507f', fontSize: '0.9rem', lineHeight: '1.55' }}>
                      Kartınızın <strong>arka yüzünde</strong>, alt kısımda basılı olan numara dizisinin <strong>ilk bölümünü</strong> (virgüllü sayıyı değil) giriniz.
                    </span>
                  </div>
                </div>
              </div>
              <div className="sa-form-group">
                <label>Program</label>
                <select className="sa-form-input" value={programType} onChange={(event) => setProgramType(event.target.value)}>
                  <option value="Fitness">Fitness</option>
                  <option value="Pool">Havuz</option>
                  <option value={WOMEN_TRAINING_PROGRAM}>Kadın Eğitim - {Number(womenTrainingFee || 0).toFixed(2)} TL</option>
                </select>
              </div>
              <div className="sa-form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Paket</label>
                <select className="sa-form-input" value={selectedPlanId} onChange={(event) => setSelectedPlanId(event.target.value)} disabled={programType === WOMEN_TRAINING_PROGRAM}>
                  {availablePlans.map((plan) => (
                    <option key={plan.id} value={String(plan.id)}>
                      {programType === WOMEN_TRAINING_PROGRAM
                        ? `${plan.displayName} - ${Number(plan.price || 0).toFixed(2)} TL`
                        : `${plan.displayName} - ${Number(plan.price || 0).toFixed(2)} TL - ${plan.creditCount} seans`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selectedPlan && (
              <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                  <div>
                    <small style={{ display: 'block', color: '#64748b' }}>Seçili Paket</small>
                    <strong style={{ color: '#0f172a' }}>{selectedPlan.displayName}</strong>
                  </div>
                  <div>
                    <small style={{ display: 'block', color: '#64748b' }}>Program</small>
                    <strong style={{ color: '#0f172a' }}>{normalizeProgramLabel(programType)}</strong>
                  </div>
                  <div>
                    <small style={{ display: 'block', color: '#64748b' }}>Seans</small>
                    <strong style={{ color: '#0f172a' }}>{programType === WOMEN_TRAINING_PROGRAM ? '-' : selectedPlan.creditCount}</strong>
                  </div>
                  <div>
                    <small style={{ display: 'block', color: '#64748b' }}>Toplam Tutar</small>
                    <strong style={{ color: '#16a34a' }}>{Number(selectedPlan.price || 0).toFixed(2)} TL</strong>
                  </div>
                </div>
              </div>
            )}

            <div className="sa-action-row" style={{ marginTop: '1rem' }}>
              <button type="button" className="sa-submit-button" onClick={handleContinue} disabled={lookupLoading}>
                Ödemeye Geç
              </button>
            </div>

            {cardInfo && (
              <div style={{ marginTop: '1.25rem', padding: '1rem', borderRadius: '14px', background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <small style={{ display: 'block', color: '#64748b' }}>Kart Sahibi</small>
                    <strong style={{ color: '#0f172a' }}>{cardInfo.firstName} {cardInfo.lastName}</strong>
                  </div>
                  <div>
                    <small style={{ display: 'block', color: '#64748b' }}>TC Kimlik</small>
                    <strong style={{ color: '#0f172a' }}>{cardInfo.identityNumber}</strong>
                  </div>
                </div>

                {Array.isArray(cardInfo.activeMemberships) && cardInfo.activeMemberships.length > 0 && (
                  <div style={{ marginTop: '1rem', display: 'grid', gap: '8px' }}>
                    {cardInfo.activeMemberships.map((membership) => (
                      <div key={membership.id} style={{ padding: '0.85rem', borderRadius: '10px', background: '#fff', border: '1px solid #dbeafe' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                          <strong style={{ color: '#1e293b' }}>{membership.programDisplayName} - {membership.planName}</strong>
                          <span style={{ color: '#16a34a', fontWeight: '700' }}>{membership.remainingCredits} seans kaldı</span>
                        </div>
                        <small style={{ color: '#64748b' }}>
                          Geçerlilik: {new Date(membership.startDate).toLocaleDateString('tr-TR')} - {new Date(membership.endDate).toLocaleDateString('tr-TR')}
                        </small>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : paymentOutcome ? (
        <div className={`sa-payment-result sa-card-result ${paymentOutcome === 'success' ? 'sa-payment-success' : 'sa-payment-failed'}`}>
          <div className={`sa-result-icon ${paymentOutcome === 'failed' ? 'error' : ''}`}>
            {paymentOutcome === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
          </div>
          <h2>{paymentOutcome === 'success' ? 'Paket Yüklendi' : 'Ödeme Başarısız'}</h2>
          <p className={`sa-result-message ${paymentOutcome === 'failed' ? 'error' : ''}`}>{resultMessage}</p>
          <div className="sa-action-row sa-card-result-actions">
            {paymentOutcome === 'failed' && (
              <button type="button" className="sa-submit-button sa-payment-action" data-variant="positive" onClick={() => { setPaymentOutcome(null); }}>
                Tekrar Dene
              </button>
            )}
            <button
              type="button"
              className="sa-btn-cancel"
              onClick={() => {
                setPaymentOutcome(null);
                setShowPaymentForm(false);
                setPaymentData(initialPaymentData);
                setPaymentErrors({});
                setPaymentHtml(null);
              }}
            >
              Yeni İşlem
            </button>
          </div>
        </div>
      ) : paymentHtml ? (
        <div className="sa-card-iframe-card">
          <div className="sa-card-iframe-header">
            <button type="button" className="sa-card-iframe-back" onClick={() => setPaymentHtml(null)}>
              <FaArrowLeft /> Geri
            </button>
            <strong>{selectedPlan ? `${Number(selectedPlan.price || 0).toFixed(2)} TL` : ''}</strong>
          </div>
          <iframe
            ref={iframeRef}
            title="3D Secure Payment"
            srcDoc={paymentHtml}
            onLoad={handleIframeLoad}
            className="sa-card-iframe"
          />
        </div>
      ) : (
        <div className="sa-appointment-section">
          <div className="sa-booking-form sa-card-form" style={{ marginTop: '20px', border: 'none', background: 'transparent', boxShadow: 'none', padding: '10px 12px 14px' }}>
            <h4 className="sa-modern-section-title">
              <span className="sa-modern-section-line"></span>
              Ödeme Bilgileri
            </h4>

            <div className="sa-modern-payment-shell sa-card-payment-shell">
              <div className="sa-modern-payment-meta">
                <div className="sa-modern-payment-meta-item">
                  <span className="sa-modern-payment-meta-label">Paket</span>
                  <strong className="sa-modern-payment-meta-value" style={{ fontSize: '0.85rem' }}>
                    {selectedPlan ? `${normalizeProgramLabel(programType)} - ${selectedPlan.displayName} / ${selectedPlan.creditCount} seans` : '-'}
                  </strong>
                </div>
                <div className="sa-modern-payment-meta-item sa-modern-payment-meta-item--amount">
                  <span className="sa-modern-payment-meta-label">Ödenecek Toplam</span>
                  <strong className="sa-modern-payment-meta-value">{selectedPlan ? Number(selectedPlan.price || 0).toFixed(2) : '0.00'} TL</strong>
                </div>
              </div>

              <div className="sa-modern-payment-layout">
                <div className="sa-modern-card-preview sa-modern-card-preview--card-app">
                  <span className="sa-modern-card-chip" aria-hidden="true"></span>
                  <div className="sa-modern-card-number">{previewCardNumber}</div>
                  <div className="sa-modern-card-footer">
                    <div>
                      <span>Kart Sahibi</span>
                      <strong>{previewCardHolder}</strong>
                    </div>
                    <div className="sa-modern-card-expiry">
                      <span>SKT</span>
                      <strong>{previewExpiry}</strong>
                    </div>
                  </div>
                  <div className="sa-modern-card-brand">
                    {detectCardType(paymentData.cardNumber) === '1' && <FaCcVisa size={28} color="#f8fafc" />}
                    {detectCardType(paymentData.cardNumber) === '2' && <FaCcMastercard size={28} color="#fed7aa" />}
                    {!detectCardType(paymentData.cardNumber) && <FaCreditCard size={24} color="#dbeafe" />}
                  </div>
                </div>

                <div className="sa-modern-payment-form">
                  <div className="sa-form-grid sa-modern-payment-grid">
                    <div className="sa-form-group sa-modern-payment-wide">
                      <label>Kart Numarası</label>
                      <div className="sa-input-with-icon">
                        <input
                          className="sa-form-input"
                          value={paymentData.cardNumber}
                          maxLength={19}
                          inputMode="numeric"
                          placeholder="0000 0000 0000 0000"
                          onChange={(event) => {
                            const raw = event.target.value.replace(/\D/g, '').slice(0, 16);
                            const formatted = raw.match(/.{1,4}/g)?.join(' ') || raw;
                            setPaymentData({ ...paymentData, cardNumber: formatted });
                          }}
                        />
                        <div className="sa-card-brand-icon">
                          {detectCardType(paymentData.cardNumber) === '1' && <FaCcVisa size={24} color="#1a1f71" />}
                          {detectCardType(paymentData.cardNumber) === '2' && <FaCcMastercard size={24} color="#f97316" />}
                          {!detectCardType(paymentData.cardNumber) && <FaCreditCard size={22} color="#94a3b8" />}
                        </div>
                      </div>
                      {paymentErrors.cardNumber && <div className="sa-error-message">{paymentErrors.cardNumber}</div>}
                    </div>

                    <div className="sa-form-group sa-modern-payment-wide">
                      <label>Kart Üzerindeki İsim</label>
                      <input
                        className="sa-form-input"
                        value={paymentData.cardName}
                        placeholder="AD SOYAD"
                        onChange={(event) => setPaymentData({ ...paymentData, cardName: event.target.value.toUpperCase() })}
                      />
                      {paymentErrors.cardName && <div className="sa-error-message">{paymentErrors.cardName}</div>}
                    </div>

                    <div className="sa-form-group">
                      <label>Ay</label>
                      <select className="sa-form-input" value={paymentData.expiryMonth} onChange={(event) => setPaymentData({ ...paymentData, expiryMonth: event.target.value })}>
                        <option value="">Ay</option>
                        {Array.from({ length: 12 }, (_, index) => {
                          const month = String(index + 1).padStart(2, '0');
                          return <option key={month} value={month}>{month}</option>;
                        })}
                      </select>
                    </div>

                    <div className="sa-form-group">
                      <label>Yıl</label>
                      <select className="sa-form-input" value={paymentData.expiryYear} onChange={(event) => setPaymentData({ ...paymentData, expiryYear: event.target.value })}>
                        <option value="">Yıl</option>
                        {Array.from({ length: 10 }, (_, index) => {
                          const year = String(new Date().getFullYear() + index);
                          return <option key={year} value={year}>{year}</option>;
                        })}
                      </select>
                    </div>

                    <div className="sa-form-group">
                      <label>CVV</label>
                      <input
                        className="sa-form-input"
                        type="password"
                        maxLength={3}
                        inputMode="numeric"
                        value={paymentData.cvv}
                        placeholder="123"
                        onChange={(event) => setPaymentData({ ...paymentData, cvv: event.target.value.replace(/\D/g, '').slice(0, 3) })}
                      />
                      {paymentErrors.cvv && <div className="sa-error-message">{paymentErrors.cvv}</div>}
                    </div>
                  </div>

                  {paymentErrors.expiry && <div className="sa-error-message">{paymentErrors.expiry}</div>}
                </div>
              </div>
            </div>

            <div className="sa-action-row sa-card-action-row">
              <button
                type="button"
                className="sa-btn-cancel"
                onClick={() => {
                  setShowPaymentForm(false);
                  setPaymentData(initialPaymentData);
                  setPaymentErrors({});
                }}
                disabled={loading}
              >
                Geri
              </button>
              <button type="button" className="sa-submit-button" onClick={handlePaymentSubmit} disabled={loading}>
                {loading ? 'İşleniyor...' : (<><FaLock style={{ marginRight: 8 }} /> Ödemeyi Tamamla</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardBalanceLoad;
